/** Normalize workspace layout to multi-tab shape (backward compatible). */

import { normalizeGlobal } from '@/widgets/servicePresets'

export function newTabId() {
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

export function blankTab(name = 'Main', widgets = []) {
  return {
    id: newTabId(),
    name: String(name || 'Main').trim() || 'Main',
    widgets: Array.isArray(widgets) ? widgets : [],
  }
}

export function normalizeLayout(raw) {
  if (!raw || typeof raw !== 'object') return raw

  const theme = raw.theme === 'dark' ? 'dark' : 'light'
  let tabs = Array.isArray(raw.tabs)
    ? raw.tabs
      .filter((t) => t && typeof t === 'object')
      .map((t, i) => ({
        id: String(t.id || `tab-${i + 1}`),
        name: String(t.name || `Layout ${i + 1}`).trim() || `Layout ${i + 1}`,
        widgets: Array.isArray(t.widgets) ? t.widgets : [],
      }))
    : []

  if (!tabs.length) {
    tabs = [blankTab('Main', Array.isArray(raw.widgets) ? raw.widgets : [])]
  }

  let activeTabId = raw.activeTabId != null ? String(raw.activeTabId) : ''
  if (!tabs.some((t) => t.id === activeTabId)) {
    activeTabId = tabs[0].id
  }

  const active = tabs.find((t) => t.id === activeTabId) || tabs[0]

  return {
    ...raw,
    version: Math.max(2, Number(raw.version) || 2),
    theme,
    gridCols: raw.gridCols || 12,
    rowHeight: raw.rowHeight || 60,
    global: normalizeGlobal(raw.global),
    tabs,
    activeTabId,
    // Mirror active tab widgets for existing canvas / widget mutations
    widgets: active.widgets || [],
  }
}

export function patchActiveWidgets(layout, widgets) {
  const normalized = normalizeLayout(layout)
  const tabs = normalized.tabs.map((t) => (
    t.id === normalized.activeTabId ? { ...t, widgets } : t
  ))
  return normalizeLayout({ ...normalized, tabs, widgets })
}

export function setActiveTab(layout, tabId) {
  const normalized = normalizeLayout(layout)
  if (!normalized.tabs.some((t) => t.id === tabId)) return normalized
  return normalizeLayout({ ...normalized, activeTabId: tabId })
}

export function addTab(layout, name) {
  const normalized = normalizeLayout(layout)
  const n = normalized.tabs.length + 1
  const tab = blankTab(name || `Layout ${n}`, [])
  return normalizeLayout({
    ...normalized,
    tabs: [...normalized.tabs, tab],
    activeTabId: tab.id,
  })
}

export function removeTab(layout, tabId) {
  const normalized = normalizeLayout(layout)
  if (normalized.tabs.length <= 1) return normalized
  const tabs = normalized.tabs.filter((t) => t.id !== tabId)
  const activeTabId = normalized.activeTabId === tabId ? tabs[0].id : normalized.activeTabId
  return normalizeLayout({ ...normalized, tabs, activeTabId })
}

export function renameTab(layout, tabId, name) {
  const normalized = normalizeLayout(layout)
  const trimmed = String(name || '').trim()
  if (!trimmed) return normalized
  const tabs = normalized.tabs.map((t) => (
    t.id === tabId ? { ...t, name: trimmed } : t
  ))
  return normalizeLayout({ ...normalized, tabs })
}

/** Accepts export payload `{ layout }` or raw layout object. */
export function parseLayoutImport(raw) {
  if (raw == null) throw new Error('Empty JSON')
  let data = raw
  if (typeof raw === 'string') {
    data = JSON.parse(raw)
  }
  if (!data || typeof data !== 'object') throw new Error('Invalid JSON')
  const layout = data.layout && typeof data.layout === 'object' ? data.layout : data
  if (!layout || typeof layout !== 'object') throw new Error('No layout found')
  if (!Array.isArray(layout.widgets) && !Array.isArray(layout.tabs)) {
    throw new Error('Layout must include widgets or tabs')
  }
  return {
    layout: normalizeLayout(layout),
    workspaceName: data.workspace?.name ? String(data.workspace.name).trim() : '',
  }
}
